import COLORS from "../colors";
import DataStructure, { sanitize } from "./DataStructure";
import ItemLocation from "./ItemLocation";
import { deepFreeze, timestampToDate } from "../util/misc";

export const UNASSIGNED_RECEIVING_FIELDS = {
  poLabel: "",
  vendor: "",
  receivedDate: null,
  receivedBy: null,
  scannedBarcodes: [],
  itemLocations: [],
  packingSlipFile: null,
  notes: "",
  status: "unassigned", // unassigned | assigned | sample | service
  assignedTo: null, // { projectID, saleID, orderID, packingSlipID }

  // Meta
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(UNASSIGNED_RECEIVING_FIELDS);

export const UNASSIGNED_STATUS_TYPES = {
  unassigned: {
    label: "warehouse.statusUnassigned",
    icon: "pending",
    color: "warning",
  },
  assigned: {
    label: "warehouse.statusAssigned",
    icon: "done",
    color: "positive",
  },
  sample: {
    label: "warehouse.statusSample",
    icon: "item_location",
    color: "info",
  },
  service: {
    label: "warehouse.statusService",
    icon: "service",
    color: "accent",
  },
};
deepFreeze(UNASSIGNED_STATUS_TYPES);

export default class UnassignedReceiving extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, UNASSIGNED_RECEIVING_FIELDS);

    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  canBeEditedBy(user) {
    return user && user.isEmployee;
  }

  canBeDeletedBy(user) {
    return user && user.isAdmin;
  }

  get statusType() {
    const type =
      UNASSIGNED_STATUS_TYPES[this.data.status] ||
      UNASSIGNED_STATUS_TYPES.unassigned;
    // `color` is a COLORS key, not a Quasar colour: spread the entry so
    // consumers get the foreground that pairs with it (warning and info are
    // light backgrounds and need dark text).
    return { ...type, ...COLORS[type.color] };
  }

  get itemCount() {
    return (this.data.scannedBarcodes || []).length;
  }

  // ItemLocationChip and its tooltip read the item type's icon and label, so
  // a location without a known item type is left out.
  get itemLocations() {
    return (this.data.itemLocations || [])
      .map((l) => new ItemLocation(l))
      .filter((l) => l.data.id && l.itemType);
  }

  get locationSummary() {
    const locs = this.data.itemLocations || [];
    if (!locs.length) return "";
    return locs
      .map((l) => {
        const parts = (l.id || "").split(":");
        return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : parts[0] || "";
      })
      .filter(Boolean)
      .join(", ");
  }

  static getPath(id) {
    if (id) return `unassignedReceiving/${id}`;
    return "unassignedReceiving";
  }

  static toFirestore(item) {
    const data = sanitize(item.data, UNASSIGNED_RECEIVING_FIELDS);
    return data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new UnassignedReceiving(snapshot.id, data);
  }
}
